import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { legacyApi, GRN, SupplierBill } from '../../../services/legacyApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { RequiredLabel } from '../../../components/forms/RequiredLabel';
import { FieldErrorText } from '../../../components/forms/FieldErrorText';
import { showValidationSummaryToast } from '../../../components/forms/ValidationSummaryToast';
import { focusFirstInvalidField, validateRequiredFields } from '../../../components/forms/formValidationUtils';
import { FlowEntryBanner } from '../../../components/workflow/FlowEntryBanner';

export const LegacySupplierBillForm: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [grns, setGrns] = useState<GRN[]>([]);
    const [existingBills, setExistingBills] = useState<SupplierBill[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [grnId, setGrnId] = useState<number | ''>('');
    const [billDate, setBillDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [referenceNo, setReferenceNo] = useState('');
    const [remarks, setRemarks] = useState('');
    const [discountAmount, setDiscountAmount] = useState<number>(0);
    const [vatPercent, setVatPercent] = useState<number>(5);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const [grnRows, billRows] = await Promise.all([
                    legacyApi.purchasing.listGRNs(),
                    legacyApi.purchasing.listBills(),
                ]);
                setGrns(grnRows || []);
                setExistingBills(billRows || []);
            } catch (error: any) {
                toast.error(error?.message || 'Failed to load bill creation data');
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, []);

    useEffect(() => {
        const prefillGrnId = Number(searchParams.get('grn_id') || 0);
        if (prefillGrnId > 0) {
            setGrnId(prefillGrnId);
        }
    }, [searchParams]);

    const availableGrns = useMemo(() => {
        const billedGrnIds = new Set(
            (existingBills || [])
                .map((b) => Number(b.grn_id))
                .filter((id) => Number.isFinite(id) && id > 0)
        );
        return (grns || []).filter((g) => !billedGrnIds.has(Number(g.grn_id)));
    }, [grns, existingBills]);

    const handleSave = async () => {
        const validation = validateRequiredFields([{ key: 'grn_id', label: 'GRN', value: grnId, required: true }]);
        if (!validation.valid) {
            setFieldErrors(validation.errorsByField);
            showValidationSummaryToast();
            focusFirstInvalidField(validation.firstInvalidField);
            return;
        }
        setFieldErrors({});
        if (vatPercent < 0 || vatPercent > 100) {
            toast.error('VAT % must be between 0 and 100');
            return;
        }
        if (discountAmount < 0) {
            toast.error('Discount cannot be negative');
            return;
        }

        try {
            setSaving(true);
            const created = await legacyApi.purchasing.createBill({
                grn_id: Number(grnId),
                bill_date: billDate,
                reference_no: referenceNo || undefined,
                remarks: remarks || undefined,
                discount_amount: discountAmount,
                vat_percent: vatPercent,
            });
            toast.success('Supplier bill created successfully');
            navigate(`/purchasing/bills/${created.bill_id}`);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to create supplier bill');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center p-8"><Loader2 className="animate-spin" /></div>;
    }

    return (
        <div className="space-y-6 w-full p-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" onClick={() => navigate('/purchasing/bills')}>
                        <ArrowLeft className="h-4 w-4 mr-2" /> Back
                    </Button>
                    <h1 className="text-2xl font-bold">New Supplier Bill</h1>
                </div>
                <Button onClick={handleSave} disabled={saving}>
                    <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Create Bill'}
                </Button>
            </div>

            <FlowEntryBanner
                title="Purchasing Flow: Supplier Billing"
                pathLabel="PO to Supplier Bill"
                nextHint="Create bill from reconciled GRN, then post and pay through AP lifecycle."
                helpModule="purchasing"
            />

            <Card>
                <CardHeader>
                    <CardTitle>Bill Header</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <RequiredLabel required htmlFor="grn_id">GRN</RequiredLabel>
                        <select
                            id="grn_id"
                            name="grn_id"
                            aria-invalid={!!fieldErrors.grn_id}
                            aria-required="true"
                            value={grnId}
                            onChange={(e) => setGrnId(Number(e.target.value))}
                            className={`w-full h-10 px-3 rounded-md border bg-background text-sm ${fieldErrors.grn_id ? 'border-red-500' : 'border-input'}`}
                        >
                            <option value="">Select GRN</option>
                            {availableGrns.map((grn) => (
                                <option key={grn.grn_id} value={grn.grn_id}>
                                    HC/GRN/{String(grn.grn_id).padStart(4, '0')} ({grn.supplier_name || 'Unknown supplier'})
                                </option>
                            ))}
                        </select>
                        <FieldErrorText id="grn_id_error" message={fieldErrors.grn_id} />
                        {availableGrns.length === 0 && (
                            <p className="text-xs text-muted-foreground">No unbilled GRNs available.</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium">Bill Date</label>
                        <input
                            type="date"
                            value={billDate}
                            onChange={(e) => setBillDate(e.target.value)}
                            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium">Reference No</label>
                        <input
                            type="text"
                            value={referenceNo}
                            onChange={(e) => setReferenceNo(e.target.value)}
                            placeholder="Vendor invoice number"
                            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium">Discount Amount</label>
                        <input
                            type="number"
                            value={discountAmount}
                            min={0}
                            onChange={(e) => setDiscountAmount(Number(e.target.value))}
                            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium">VAT %</label>
                        <input
                            type="number"
                            value={vatPercent}
                            min={0}
                            max={100}
                            onChange={(e) => setVatPercent(Number(e.target.value))}
                            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                        />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                        <label className="text-sm font-medium">Remarks</label>
                        <input
                            type="text"
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                        />
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};
